import { getDevProps } from '@components/DevToolbar/helpers/getDevProps';
import { BASE_PATH, PIMCORE_LOCALE_FALLBACK, PUBLIC_HOST, REVALIDATION_TIME, SUPPORTED_OFFERS } from '@config/index';
import { DEFAULT_LOCALE, DISPLAY_ECO_TAX_INFO, DISPLAY_TRUSTMATE_REVIEWS } from '@const/index';
import NextProductService from '@features/api/nextApiService/nextProductService';
import type { TCategoriesTree } from '@features/categories/typescript';
import type { ICmsDocument } from '@features/cms/typescript';
import { getLayoutData } from '@features/layout/api';
import layoutUiState from '@features/layout/mobX/LayoutUiState';
import { getMarketingCategories, getMetaData, getProductConfig, getProductFrontConfig, getProductInspirationTagIds, getRecommendedProducts, getRelatedProducts, getSectionsData } from '@features/productPreview/api';
import ProductPreviewContent from '@features/productPreview/components/ProductPreviewContent';
import { uiState } from '@features/productPreview/mobX/state/context';
import ProductBreadcrumbs from '@features/productPreview/sections/ProductBreadcrumbs/ProductBreadcrumbs';
import StickyBar from '@features/productPreview/sections/StickyBar/StickyBar';
import type { IJsonProductSchema, IMarketingCategoryResponse, IMetaData, IProductConfigResponse, IRecommendedProductResponse, IRichContent, ISection } from '@features/productPreview/typescript';
import { useResponsive } from '@features/responsive';
import SelfCanonicalLink from '@features/seo/SelfCanonicalLink/SelfCanonicalLink';
import ReaffiliatesProductCard from '@reaffiliates/ReaffiliatesProductCard';
import type { TLocale } from '@typescript/index';
import { getCache } from '@utils/cacheHelpers';
import { toISO4217CurrencyCode } from '@utils/formatPrice';
import getOffer from '@utils/getOffer';
import getProductShareImage from '@utils/getProductShareImage';
import getProductIDBySlug from '@utils/getProductIDBySlug';
import getProductUrl from '@utils/getProductUrl';
import getPublicEnv from '@utils/getPublicEnv';
import logger from '@utils/logger';
import { logScrollTrigger } from '@utils/scrollDebug';
import openGraphUrlCreator from '@utils/openGraphUrlCreator';
import getArticles from '@utils/pages/getArticles';
import getArticlesWithTags from '@utils/pages/getArticlesWithTags';
import { trackRevalidation } from '@utils/revalidationTracker';
import { getSafeProductPagePath, sanitizeCart, sanitizeObjectUuid, sanitizePresetIndex, sanitizeProductSlug, sanitizeTrackingValue } from '@utils/sanitizeProductQueryParams';
import type { IInitOptions, IProductConfiguration } from '@vox/front-configurator';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import type { GetServerSideProps, GetServerSidePropsContext } from 'next/types';
import { serverSideTranslations } from '@utils/i18n/serverSideTranslations';
import { ImageJsonLd, NextSeo, ProductJsonLd } from 'next-seo';
import { useEffect, useRef, useState } from 'react';
export type PropsType = {
  sections: ISection[];
  productID: number;
  marketingCategoriesResponse: IMarketingCategoryResponse;
  articles: ICmsDocument[];
  categoriesTree: TCategoriesTree;
  metaData: IMetaData;
  richContent: IRichContent | null;
  configuratorData: IProductConfiguration | null;
  configuratorConfig: IInitOptions | null;
  isCreativeProduct?: boolean;
  relatedProductsResponse: IRecommendedProductResponse | null;
  recommendedNonDecorationResponse: IRecommendedProductResponse | null;
  recommendedDecorationResponse: IRecommendedProductResponse | null;
  productJsonSchema: IJsonProductSchema | null;
  config: IProductConfigResponse | null;
};
const ProductDynamic = ({
  sections,
  productID,
  marketingCategoriesResponse,
  articles,
  categoriesTree,
  metaData,
  richContent,
  configuratorData,
  configuratorConfig,
  isCreativeProduct,
  relatedProductsResponse,
  recommendedNonDecorationResponse,
  recommendedDecorationResponse,
  productJsonSchema,
  config
}: PropsType) => {
  const [isAddToCartDisabled, setIsAddToCartDisabled] = useState(true);
  const [productPrice, setProductPrice] = useState('');
  const {
    hideSlider,
    productName
  } = uiState;
  const {
    asPath,
    locale
  } = useRouter();
  const router = useRouter();
  const {
    isWiderThanDesktop
  } = useResponsive();
  const {
    setLastVisitedProductId,
    setListingScrollRestorePending,
    setIsProductCardOpened
  } = layoutUiState;
  const configuratorRef = useRef<HTMLDivElement>(null);
  const hasCalledReaffiliates = useRef(false);
  useEffect(() => {
    const safePath = getSafeProductPagePath(asPath);
    if (typeof window !== 'undefined' && asPath !== safePath) {
      window.history.replaceState(null, '', safePath);
    }
  }, [asPath]);
  useEffect(() => {
    const handleRouteChange = () => {
      hideSlider();
    };
    router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router, hideSlider]);
  useEffect(() => {
    setIsProductCardOpened(true);
    return () => {
      setIsProductCardOpened(false);
    };
  }, [setIsProductCardOpened]);
  useEffect(() => {
    // Keep previous id until browser back — beforePopState overwrites it for this product.
    // Clearing on mount broke return-to-listing scroll (especially with ProductPreviewContent race).
    router.beforePopState(({
      as
    }) => {
      if (as !== router.asPath) {
        logScrollTrigger('product:beforePopState — setLastVisitedProductId', {
          productID,
          from: router.asPath,
          to: as
        });
        setLastVisitedProductId(productID);
        setListingScrollRestorePending(true);
      }
      return true;
    });
    return () => {
      router.beforePopState(() => true);
    };
  }, [router, productID, setLastVisitedProductId, setListingScrollRestorePending]);
  useEffect(() => {
    if (productID && productName && productPrice && marketingCategoriesResponse && marketingCategoriesResponse.marketingCategories && marketingCategoriesResponse.marketingCategories.length > 0 && !hasCalledReaffiliates.current && productJsonSchema) {
      hasCalledReaffiliates.current = true;
      ReaffiliatesProductCard(productID, productName, productPrice, marketingCategoriesResponse.marketingCategories, productJsonSchema.brand);
    }
  }, [productID, productName, productPrice, marketingCategoriesResponse, productJsonSchema]);
  useEffect(() => {
    if (marketingCategoriesResponse?.productName) {
      uiState.setProductName(marketingCategoriesResponse.productName);
    }
    if (metaData?.price) {
      setProductPrice(metaData.price);
    }
    hasCalledReaffiliates.current = false;
  }, [marketingCategoriesResponse, metaData, productID]);
  const generalStyles = {
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    flexGrow: 1
  };
  const stickyBarLinks: Array<string> = [];
  const getProductImagesData = (): ImageJsonLd[] => {
    if (!Array.isArray(sections)) return [];
    const productSlides = sections.find(section => section.type === 'PRODUCT_SLIDES');
    if (!productSlides?.items?.[0]?.slides) return [];
    return productSlides.items[0].slides.map(slide => ({
      contentUrl: slide.desktop,
      creator: {
        '@type': 'Organisation',
        name: 'VOX'
      },
      copyrightNotice: '© VOX'
    }));
  };
  const getProductImagesArray = (): string[] => {
    if (!Array.isArray(sections)) return [];
    const productSlides = sections.find(section => section.type === 'PRODUCT_SLIDES');
    if (!productSlides?.items?.[0]?.slides) return [];
    return productSlides.items[0].slides.map(slide => slide?.desktop || '');
  };
  const shareImage = metaData ? getProductShareImage(metaData, sections) : '';
  return <>
      <SelfCanonicalLink />
      <main id="main-content" sx={generalStyles}>
        {metaData && <>
            <NextSeo title={metaData.title} titleTemplate="%s | VOX" description={metaData.description ? metaData.description.slice(0, 152) : ''} openGraph={{
          type: 'Website',
          url: openGraphUrlCreator(getSafeProductPagePath(asPath), locale),
          title: metaData.title,
          description: metaData.description ? metaData.description.slice(0, 152) : '',
          images: [{
            url: shareImage,
            width: 1200,
            height: 630,
            alt: metaData.name || metaData.title,
            type: 'image/jpeg'
          }]
        }} />
            <ImageJsonLd images={getProductImagesData()} />
            {productJsonSchema && <ProductJsonLd productName={productJsonSchema.name} description={productJsonSchema.description} images={[productJsonSchema.image, ...getProductImagesArray()]} brand={productJsonSchema.brand} offers={productJsonSchema.offers} aggregateRating={productJsonSchema.aggregateRating} reviews={productJsonSchema.review} />}
          </>}
        <StickyBar stickyBarLinks={stickyBarLinks} isDesktop={!!isWiderThanDesktop} configuratorRef={configuratorRef} setProductPrice={setProductPrice} isAddToCartDisabled={isAddToCartDisabled} handleConfiguratorLoading={setIsAddToCartDisabled} />
        {marketingCategoriesResponse && <ProductBreadcrumbs marketingCategoriesResponse={marketingCategoriesResponse} categoriesTree={categoriesTree} />}
        {sections && productID && metaData && <ProductPreviewContent sections={sections} productID={productID} metaData={metaData} richContent={richContent} configuratorData={configuratorData} configuratorConfig={configuratorConfig} isCreativeProduct={isCreativeProduct} relatedProductsResponse={relatedProductsResponse} recommendedNonDecorationResponse={recommendedNonDecorationResponse} recommendedDecorationResponse={recommendedDecorationResponse} configuratorRef={configuratorRef} articles={articles} showTrustMateReviews={DISPLAY_TRUSTMATE_REVIEWS === 'true'} config={config} />}
      </main>
    </>;
};
export default observer(ProductDynamic);
function firstDynamicQueryValue(value: string | string[] | undefined): string | null {
  if (value === undefined) return null;
  if (Array.isArray(value)) return value[0] ?? null;
  return value;
}
export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext) => {
  const {
    params,
    locale,
    resolvedUrl,
    query
  } = context;

  /** Query z Next (niezawodniejsze niż samo resolvedUrl przy i18n / basePath / proxy). */
  let presetIndex = sanitizePresetIndex(firstDynamicQueryValue(query.presetIndex));
  let cart = sanitizeCart(firstDynamicQueryValue(query.cart));
  let objectUuid = sanitizeObjectUuid(firstDynamicQueryValue(query.objectUuid));
  if (resolvedUrl.includes('?')) {
    const queryParams = new URLSearchParams(resolvedUrl.split('?')[1] || '');
    if (presetIndex === null) presetIndex = sanitizePresetIndex(queryParams.get('presetIndex'));
    if (cart === null) cart = sanitizeCart(queryParams.get('cart'));
    if (objectUuid === null) objectUuid = sanitizeObjectUuid(queryParams.get('objectUuid'));
  }
  const reqUrl = context.req?.url;
  if (reqUrl?.includes('?')) {
    const qs = reqUrl.slice(reqUrl.indexOf('?') + 1);
    const reqParams = new URLSearchParams(qs);
    if (presetIndex === null) presetIndex = sanitizePresetIndex(reqParams.get('presetIndex'));
    if (cart === null) cart = sanitizeCart(reqParams.get('cart'));
    if (objectUuid === null) objectUuid = sanitizeObjectUuid(reqParams.get('objectUuid'));
  }
  const productSlug = params?.slug as string;
  const langLocale = locale ? PIMCORE_LOCALE_FALLBACK[locale] : PIMCORE_LOCALE_FALLBACK[getPublicEnv('NEXT_PUBLIC_DEFAULT_LOCALE')];
  const cacheKey = `${productSlug}-${locale}`;
  const cachePropsTime = process.env.PRODUCT_PROPS_REVALIDATION_TIME ? parseInt(process.env.PRODUCT_PROPS_REVALIDATION_TIME, 10) : 600;
  const cacheLongPropsTime = process.env.LONG_PRODUCT_PROPS_REVALIDATION_TIME ? parseInt(process.env.LONG_PRODUCT_PROPS_REVALIDATION_TIME, 10) : 14400;
  const slugParam = sanitizeProductSlug(params?.slug as string);
  const productID = getProductIDBySlug(slugParam);
  if (!productID) return {
    props: {},
    notFound: true
  };
  const [offer, priceList, storage] = getOffer();
  let sections = null;
  let marketingCategoriesResponse: IMarketingCategoryResponse | null = null;
  let metaData: IMetaData | null = null;
  const schemaObjectUuid = objectUuid as string || presetIndex as string || cart as string || undefined;
  try {
    sections = await getCache(`${cacheKey}-sections`, () => getSectionsData(productID, offer, priceList, storage, langLocale), cachePropsTime, true);
    if (!sections) {
      console.debug(`[${productSlug}] ProductPage returning 404 - no sections found`);
      return {
        props: {},
        notFound: true
      };
    }
    metaData = await getCache(`${cacheKey}-${schemaObjectUuid}-metaData`, () => getMetaData(productID, offer, priceList, storage, langLocale, schemaObjectUuid ? schemaObjectUuid : undefined), cachePropsTime, true);
  } catch (error) {
    console.debug(`[${productSlug}] ProductPage returning error - error found `, {
      error
    });
    throw error instanceof Error ? error : new Error(String(error));
  }
  const properProductSlug = getProductUrl(metaData?.name || '', productID);
  if (`/product/${slugParam}` !== properProductSlug) {
    // Zachowaj parametry śledzenia Google Ads podczas redirectu
    const currentUrlParams = new URLSearchParams(resolvedUrl.split('?')[1] || '');
    const trackingParams = new URLSearchParams();

    // Zachowaj parametry śledzenia – tylko dozwolone znaki (ochrona przed XSS)
    const trackingParamNames = ['gclid', 'glcid', 'gclsrc', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'fbclid', 'msclkid', 'ref'];
    trackingParamNames.forEach(param => {
      const value = currentUrlParams.get(param);
      if (value) {
        const safe = sanitizeTrackingValue(value);
        if (safe) trackingParams.set(param, safe);
      }
    });

    // Dodaj parametry aplikacji
    if (cart) trackingParams.set('cart', cart);
    if (presetIndex) trackingParams.set('presetIndex', presetIndex);
    if (objectUuid) trackingParams.set('objectUuid', objectUuid);
    const queryString = trackingParams.toString();
    const redirectUrl = locale && locale !== DEFAULT_LOCALE ? `/${locale}/${properProductSlug}${queryString ? `?${queryString}` : ''}` : `${properProductSlug}${queryString ? `?${queryString}` : ''}`;
    return {
      redirect: {
        destination: redirectUrl,
        permanent: true
      }
    };
  }
  let richContent: IRichContent | null = null;
  if (locale === 'pl') richContent = await getCache(`${cacheKey}-richContent`, () => NextProductService.getRichContent(productID), cachePropsTime, true);
  try {
    marketingCategoriesResponse = await getCache(`${cacheKey}-marketingCategoriesResponse`, () => getMarketingCategories(productID, offer, priceList, storage, langLocale), cachePropsTime, true);
  } catch (error) {
    console.debug(`[${productSlug}] ProductPage returning error - error found `, {
      error
    });
    throw error instanceof Error ? error : new Error(String(error));
  }
  const config = await getCache(`${cacheKey}-config`, () => getProductConfig(productID, langLocale, offer, storage, priceList), cachePropsTime, true);
  let configuratorConfig: IInitOptions | null = null;
  let configuratorData: IProductConfiguration | null = null;
  configuratorData = await getCache(`${cacheKey}-configuratorData`, () => getProductFrontConfig(productID, langLocale, offer, storage, priceList), cachePropsTime, true);
  // FC tłumaczenia przez BFF ecommerce (Redis/plik → GCS), ten sam cache co vox-pl
  // Override: NEXT_PUBLIC_FC_TRANSLATIONS_LOAD_PATH (musi zawierać {{lng}} {{ns}})
  const fcTranslationsLoadPath = process.env.NEXT_PUBLIC_FC_TRANSLATIONS_LOAD_PATH || `${BASE_PATH}/api/i18n/{{lng}}/{{ns}}?project=front-configurator`;
  configuratorConfig = {
    productId: productID,
    objectUuid: objectUuid || '',
    cart: cart || '',
    region: {
      offer,
      pricelists: priceList,
      storages: storage
    },
    lang: langLocale,
    isShop: 'true',
    withGallery: process.env.FC_WITH_GALLERY === 'true',
    showDeliveryInfo: process.env.FC_SHOW_DELIVERY_INFO === 'true',
    ecoTaxInfo: DISPLAY_ECO_TAX_INFO === 'true',
    withStorBanner: process.env.FC_WITH_STOR_BANNER === 'true',
    seeInShowroomActive: (config?.opts?.showShopAvailabilityInfo && offer === SUPPORTED_OFFERS.pl) ?? false,
    ...(config?.type === 'creative' ? {
      configuratorType: 'creative' as const
    } : {}),
    ...(fcTranslationsLoadPath ? {
      translationsLoadPath: fcTranslationsLoadPath
    } : {})
  };
  const relatedProductsResponse = await getCache(`${cacheKey}-relatedProductsResponse`, () => getRelatedProducts(productID, offer, priceList, storage, langLocale), cacheLongPropsTime, true);
  const recommendedNonDecorationResponse = await getCache(`${cacheKey}-recommendedNonDecorationResponse`, () => getRecommendedProducts(productID, offer, priceList, storage, langLocale, false), cacheLongPropsTime, true);
  const recommendedDecorationResponse = await getCache(`${cacheKey}-recommendedDecorationResponse`, () => getRecommendedProducts(productID, offer, priceList, storage, langLocale, true), cacheLongPropsTime, true);
  const productInspirationTagIds = await getCache(`${cacheKey}-productInspirationTagIds`, () => getProductInspirationTagIds(productID, offer, priceList, storage, langLocale), cacheLongPropsTime, true);
  let articles = [];
  if (productInspirationTagIds && productInspirationTagIds.length) {
    const articlesWithTagsResult = await getCache(`${cacheKey}-articlesWithTags}`, () => getArticlesWithTags(productInspirationTagIds, 'ideas'), cacheLongPropsTime, true);
    articles = articlesWithTagsResult?.documents && articlesWithTagsResult.documents.length ? articlesWithTagsResult.documents.slice(0, Math.min(3, articlesWithTagsResult.documents.length)) : [];
    if (articles.length < 3) {
      const articlesResult = await getArticles(3, 'ideas');
      articles = articles.concat(articlesResult?.documents && articlesResult.documents.length ? articlesResult.documents : []);
      articles = articles.slice(0, Math.min(3, articles.length));
    }
  }
  if (articles.length < 3) {
    const articlesResult = await getCache(`3-new-articles`, () => getArticles(3, 'ideas'), cacheLongPropsTime, true);
    articles = articles.concat(articlesResult?.documents && articlesResult.documents.length ? articlesResult.documents : []);
    articles = articles.slice(0, Math.min(3, articles.length));
  }
  try {
    await trackRevalidation(`product/${slugParam}`, locale as string, REVALIDATION_TIME, 'static', 'product');
  } catch (error) {
    logger.error('Failed to track revalidation:', error);
  }
  let newProductJsonSchema: IJsonProductSchema | null = null;
  if (metaData) {
    const safeQuery = new URLSearchParams();
    if (cart) safeQuery.set('cart', cart);
    if (presetIndex) safeQuery.set('presetIndex', presetIndex);
    if (objectUuid) safeQuery.set('objectUuid', objectUuid);
    const safePath = (locale && locale !== DEFAULT_LOCALE ? `/${locale}` : '') + properProductSlug + (safeQuery.toString() ? `?${safeQuery.toString()}` : '');
    const productFullUrl = `${PUBLIC_HOST}${safePath}`;
    if (metaData.price) {
      newProductJsonSchema = {
        name: metaData.name,
        image: getProductShareImage(metaData, sections),
        brand: metaData.brand,
        ...(metaData.trustmateData?.review && metaData.trustmateData.review.length > 0 && {
          review: metaData.trustmateData.review
        }),
        ...(metaData.trustmateData?.aggregateRating && {
          aggregateRating: metaData.trustmateData.aggregateRating
        }),
        offers: {
          price: Number(metaData.price.replace(',', '.')),
          priceCurrency: toISO4217CurrencyCode(metaData.priceSymbol),
          availability: metaData.availability === 'available' ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
          url: productFullUrl,
          condition: 'https://schema.org/NewCondition'
        },
        description: metaData.description
      };
      if (metaData.basePrice && metaData.basePrice !== metaData.price) {
        newProductJsonSchema.offers.priceSpecification = {
          '@type': 'UnitPriceSpecification',
          priceType: 'https://schema.org/ListPrice',
          price: Number(metaData.basePrice.replace(',', '.')),
          priceCurrency: toISO4217CurrencyCode(metaData.priceSymbol)
        };
      }
    }
  }
  const props = {
    ...getDevProps('static', REVALIDATION_TIME),
    marketingCategoriesResponse,
    sections,
    productID,
    metaData,
    richContent,
    ...(await getLayoutData(locale as TLocale)),
    ...(await serverSideTranslations(locale || DEFAULT_LOCALE)),
    configuratorData,
    configuratorConfig,
    isCreativeProduct: config?.type === 'creative',
    relatedProductsResponse,
    recommendedNonDecorationResponse,
    recommendedDecorationResponse,
    articles,
    productJsonSchema: newProductJsonSchema,
    config
  };
  return {
    props
  };
};